import { Avatar, AvatarFallback } from "@/components/ui/library/avatar";
import { ChatMessageBubble } from "./ChatMessageBubble";
import { Pencil, Reply, Trash2 } from "lucide-react";

export interface MessageDisplay {
  id: string;
  text: string;
  time: string;
  author?: string;
  /** "Switch user": the account this was written into, when the author above
   *  is the manager who was viewing as it. Renders as "· as ANOOJA". */
  authorVia?: string;
  role?: string;
  isSent: boolean;
  avatar?: string;
  /** Delete-for-everyone tombstone — renders "This message was deleted". */
  isDeleted?: boolean;
  /** Sender edited the text — renders an "edited" tag next to the time. */
  isEdited?: boolean;
  file?: { name: string; type: string };
  mediaUrl?: string;
  readBy?: { userId: number; readAt: string }[];
  mentions?: { userId: number; name: string }[] | null;
  replyTo?: {
    id: string;
    body: string | null;
    type: string;
    sender: { id: number; name: string };
    switchedByUser?: { id: number; name: string } | null;
  };
}

interface ChatMessageProps {
  message: MessageDisplay;
  onReply?: () => void;
  /** Delete-for-everyone — passed only for the viewer's own live messages. */
  onDelete?: () => void;
  /** Edit — passed only for the viewer's own live TEXT messages. */
  onEdit?: () => void;
  /** Reply-preview click → scroll to the quoted message. */
  onJumpTo?: (messageId: string) => void;
}

export function ChatMessage({
  message,
  onReply,
  onDelete,
  onEdit,
  onJumpTo,
}: ChatMessageProps) {
  return (
    <div
      data-message-id={message.id}
      className={`group flex gap-3 items-start w-full rounded-[12px] transition-colors ${
        message.isSent ? "justify-end" : "justify-start"
      }`}
    >
      {/* Hover actions — left side for sent messages (delete + edit + reply) */}
      {message.isSent && !message.isDeleted && (
        <div className="flex items-center gap-0.5 self-center">
          {onEdit && (
            <button
              onClick={onEdit}
              className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-[#e6e8e7] transition-all"
              title="Edit"
            >
              <Pencil size={14} className="text-[#9ba3a0] hover:text-[#374741]" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-[#fbe9ec] transition-all"
              title="Delete for everyone"
            >
              <Trash2 size={14} className="text-[#9ba3a0] hover:text-[#da2a46]" />
            </button>
          )}
          {onReply && (
            <button
              onClick={onReply}
              className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-[#e6e8e7] transition-all"
              title="Reply"
            >
              <Reply size={14} className="text-[#9ba3a0]" />
            </button>
          )}
        </div>
      )}

      {!message.isSent && (
        <Avatar className="size-10 bg-[var(--color-toggle-inactive)] shrink-0">
          <AvatarFallback className="text-[16px] font-medium text-[#0e121b] tracking-[-0.176px] leading-[24px]">
            {message.avatar || "U"}
          </AvatarFallback>
        </Avatar>
      )}

      <ChatMessageBubble message={message} onJumpTo={onJumpTo} />

      {/* Reply button — right side for received messages */}
      {!message.isSent && !message.isDeleted && onReply && (
        <button
          onClick={onReply}
          className="opacity-0 group-hover:opacity-100 self-center p-1 rounded-full hover:bg-[#e6e8e7] transition-all"
          title="Reply"
        >
          <Reply size={14} className="text-[#9ba3a0]" />
        </button>
      )}
    </div>
  );
}
